import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  SquarePen,
  X,
} from "lucide-react";
import { ChatComposer, type ChatSendInput } from "@/components/ChatComposer";
import { Avatar, hueFor, ChatMessageBubbles } from "@/components/ChatMessageBubbles";
import { ChatMessageMenu } from "@/components/ChatMessageMenu";
import { ChatNewDmPanel } from "@/components/ChatNewDmPanel";
import { ModuleDrawerAdd } from "@/components/ModuleDrawerAdd";
import { ApiError } from "@/lib/api";
import {
  CHAT_FOLDERS,
  chatAccountLabel,
  chatActionError,
  chatCapabilities,
  chatFolderForChannel,
  chatGroupTitle,
  connectUrl,
  deleteChatMessage,
  editChatMessage,
  fetchChatAccounts,
  fetchChatActivity,
  fetchChatChannels,
  fetchChatPermalink,
  fetchChatThread,
  fetchChatThreadReplies,
  fetchChatStatus,
  markChatRead,
  providerConfigured,
  sendChatMessage,
  setChatReaction,
  startChatConnect,
  type ChatAccount,
  type ChatActivity,
  type ChatActivitySnapshot,
  type ChatChannel,
  type ChatFolderId,
  type ChatMessage,
  type ChatProviderId,
  type ChatStatus,
} from "@/lib/chatConnect";
import { matchesHubSearch, useHubSearchQuery } from "@/lib/hubSearch";
import { formatListWhen } from "@/lib/listWhen";
import {
  peekModuleCache,
  pruneModuleCache,
  readModuleCache,
  writeModuleCache,
} from "@/lib/moduleCache";
import { emojiForReactionName, replaceShortcodes, useSlackEmoji } from "@/lib/emoji";
import { isHubIosShell } from "@/lib/hubShell";
import { openHrefInNewTab } from "@/lib/openExternalLink";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import {
  NEEDS_YOU_PILOT_MESSAGE,
  fetchSlackCustomerMessages,
  fetchSlackCustomerStatus,
  isListedSlackCustomer,
  isSlackCustomerAccount,
  slackErrorPermalink,
  slackRetryAfter,
  useNeedsYou,
  type NeedsYouItem,
  type SlackCustomerStatus,
} from "@/lib/slackCustomer";
import { MAIL_CLOCK_FORMAT_EVENT, readMailClockFormat, type MailClockFormatId } from "@/mail/prefs";
import { PaneResizer } from "@/components/PaneResizer";

/** Fallback copy from Kyle target shot 28 — shown when API omits setupSteps. */
const DEFAULT_SLACK_SETUP_STEPS = [
  "Open https://api.slack.com/apps and create an app (From scratch) for your workspace.",
  "Under OAuth & Permissions, add redirect URL: https://api.hub.atw.net/oauth/slack/callback",
  "Add user token scopes: channels:history, channels:read, chat:write, groups:history, groups:read, im:history, im:read, im:write, mpim:history, mpim:read, mpim:write, search:read, users:read.",
  "Copy Client ID and Client Secret into ~/.config/atw-hub/slack.env as SLACK_CLIENT_ID and SLACK_CLIENT_SECRET (and the same keys on the Lightsail API host).",
  "Restart the Hub API, then click Connect Slack in Settings. Connect again to add a second Slack login.",
];

const NAV_COLLAPSE_KEY = "atw-hub-chat-nav-collapsed";
const ALL_BRANCH = "__all";
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Sort fields — one row each; direction toggles via ↑/↓ like Mail. Unread first is the default. */
const SORT_FIELDS = [
  { id: "unread", label: "Unread first" },
  { id: "recent", label: "Recent" },
  { id: "name", label: "Name" },
] as const;

type SortFieldId = (typeof SORT_FIELDS)[number]["id"];
type SortDirection = "desc" | "asc";

const FILTER_OPTIONS = [
  { id: "all", label: "All Chats" },
  { id: "unread", label: "Unread" },
  { id: "week", label: "Active This Week" },
] as const;

type FilterId = (typeof FILTER_OPTIONS)[number]["id"];

/** Conversation type across providers (Cliq chats count as direct messages). */
type ChatType = "direct" | "group" | "channels";

const ALL_FOLDERS: { id: ChatType; label: string }[] = [
  { id: "direct", label: "Direct messages" },
  { id: "group", label: "Group messages" },
  { id: "channels", label: "Channels" },
];

/** Every conversation in one account, whatever its folder. */
const ALL_MESSAGES = "all";
const ALL_MESSAGES_LABEL = "All Messages";

type ListView =
  | { scope: "home" }
  | { scope: "all"; folder: "everything" | ChatType }
  | { scope: "account"; accountId: string; folder: ChatFolderId | typeof ALL_MESSAGES };

type ChatRow = {
  key: string;
  account: ChatAccount;
  providerId: ChatProviderId;
  channel: ChatChannel;
  folder: ChatFolderId;
  type: ChatType;
  title: string;
  sortName: string;
  activity?: ChatActivity;
  /** Newest message, when known. */
  at: number | null;
  /** Newest message, else when you last read it — keeps Recent useful when Slack limits history. */
  orderAt: number | null;
  unread: boolean;
  /** Messages since the last read; 0 when the provider doesn't count them. */
  unreadCount: number;
  preview: string;
};

type ListSection = { id: string; label: string; rows: ChatRow[] };

function chatRowMatchesSearch(row: ChatRow, query: string): boolean {
  return matchesHubSearch(
    query,
    row.title,
    row.channel.name,
    row.channel.topic,
    row.preview,
    row.activity?.latestAuthor,
    row.account.email,
  );
}

/** Customer-mode conversation paging and its Slack link (pilot shows only the latest page). */
type ThreadInfo = { key: string; hasMore: boolean; nextCursor?: string; limited: boolean; permalink?: string };

/** Slack rate-limited the open conversation; Hub retries at `until`. */
type RateLimit = { key: string; until: number; permalink?: string };

type NeedsRow = {
  key: string;
  account: ChatAccount;
  item: NeedsYouItem;
  row?: ChatRow;
  title: string;
  preview: string;
};

function providerOf(account: ChatAccount | undefined): ChatProviderId {
  const id = account?.providerId;
  if (id === "cliq" || id === "zoom" || id === "teams" || id === "google") return id;
  return "slack";
}

function providerName(providerId: ChatProviderId): string {
  switch (providerId) {
    case "cliq":
      return "Zoho Cliq";
    case "zoom":
      return "Zoom Team Chat";
    case "teams":
      return "Microsoft Teams";
    case "google":
      return "Google Chat";
    default:
      return "Slack";
  }
}

/** Optimistic reaction toggle; `on` adds the connected account's reaction. */
function withReaction(message: ChatMessage, name: string, on: boolean): ChatMessage {
  const current = message.reactions || [];
  const existing = current.find((item) => item.name === name);
  let next: NonNullable<ChatMessage["reactions"]>;
  if (existing) {
    next = current
      .map((item) =>
        item.name === name
          ? { ...item, count: Math.max(0, item.count + (on ? (item.mine ? 0 : 1) : item.mine ? -1 : 0)), mine: on }
          : item,
      )
      .filter((item) => item.count > 0);
  } else if (on) {
    next = [...current, { emoji: emojiForReactionName(name) || `:${name}:`, name, count: 1, mine: true }];
  } else {
    next = current;
  }
  return { ...message, reactions: next.length ? next : undefined };
}

/** Open a provider link fetched after the click without tripping popup blockers. */
async function openFetchedLink(load: () => Promise<string>): Promise<boolean> {
  if (isHubIosShell()) {
    try {
      const href = await load();
      if (!href) throw new Error("no link");
      openHrefInNewTab(href);
      return true;
    } catch {
      return false;
    }
  }
  const tab = window.open("about:blank", "_blank");
  try {
    const href = await load();
    if (!href) throw new Error("no link");
    if (tab) {
      tab.opener = null;
      tab.location.href = href;
    } else {
      openHrefInNewTab(href);
    }
    return true;
  } catch {
    tab?.close();
    return false;
  }
}

function channelKey(accountId: string, channelId: string): string {
  return `${accountId}:${channelId}`;
}

function chatType(folder: ChatFolderId): ChatType {
  if (folder === "group") return "group";
  if (folder === "direct" || folder === "chats") return "direct";
  return "channels";
}

function typeLabel(type: ChatType): string {
  return ALL_FOLDERS.find((f) => f.id === type)?.label || "Chats";
}

function conversationTitle(channel: ChatChannel, type: ChatType, selfHandle?: string): string {
  const name = channel.name || channel.id;
  if (type === "channels") return name.startsWith("#") ? name : `#${name}`;
  if (type === "group") return chatGroupTitle(name, selfHandle);
  return name;
}

function previewFor(type: ChatType, channel: ChatChannel, activity?: ChatActivity): string {
  if (activity?.latestText) {
    const author = activity.latestAuthor;
    if (author === "You") return `You: ${activity.latestText}`;
    return type !== "direct" && author ? `${author}: ${activity.latestText}` : activity.latestText;
  }
  return type === "channels" ? channel.topic || "" : "";
}

/** Bubble text: the count, capped like Slack and Mail; empty when only "unread" is known. */
function unreadBadgeText(count: number): string {
  if (count <= 0) return "";
  return count > 99 ? "99+" : String(count);
}

function unreadLabel(count: number): string {
  if (count <= 0) return "Unread";
  if (count > 99) return "More than 99 unread messages";
  return count === 1 ? "1 unread message" : `${count} unread messages`;
}


/** Leading mark for conversation list rows: # for channels, avatar/initials for people and groups. */
function ChatListFace({
  type,
  title,
  avatarUrl,
  faceKey,
}: {
  type: ChatType;
  title: string;
  avatarUrl?: string;
  faceKey: string;
}): ReactNode {
  if (type === "channels") {
    return (
      <span className="chat-row-face chat-row-face-channel" aria-hidden>
        #
      </span>
    );
  }
  const name = title.replace(/^#/, "").trim() || "?";
  const url = type === "direct" && avatarUrl && /^https:\/\//i.test(avatarUrl) ? avatarUrl : undefined;
  return (
    <span className="chat-row-face">
      <Avatar name={name} url={url} hue={hueFor(faceKey)} />
    </span>
  );
}

function compareRows(a: ChatRow, b: ChatRow, field: SortFieldId, dir: SortDirection): number {
  // Unread stays on top in both directions; ↑/↓ only flips the time order inside each group.
  if (field === "unread" && a.unread !== b.unread) return a.unread ? -1 : 1;
  if (field === "unread" || field === "recent") {
    if (a.orderAt !== b.orderAt) {
      if (a.orderAt == null) return 1;
      if (b.orderAt == null) return -1;
      return dir === "desc" ? b.orderAt - a.orderAt : a.orderAt - b.orderAt;
    }
    return a.sortName.localeCompare(b.sortName);
  }
  const byName = a.sortName.localeCompare(b.sortName, undefined, { sensitivity: "base" });
  return dir === "asc" ? byName : -byName;
}

function sortDirAria(field: SortFieldId, dir: SortDirection): string {
  if (field === "unread") return dir === "desc" ? "unread first, then newest" : "unread first, then oldest";
  if (field === "recent") return dir === "desc" ? "newest first" : "oldest first";
  return dir === "desc" ? "Z to A" : "A to Z";
}

/** Customer-mode conversations load live through the paged route; DMs are marked read on open. */
function threadLoadOptions(row: ChatRow): { customer: boolean; markRead: boolean } | undefined {
  return isSlackCustomerAccount(row.account) ? { customer: true, markRead: row.type !== "channels" } : undefined;
}

function newestTs(messages: ChatMessage[]): string | undefined {
  let bestAt = -1;
  let best: string | undefined;
  for (const message of messages) {
    const at = message.at ?? (message.ts && /^\d+\.\d+$/.test(message.ts) ? Number(message.ts) * 1000 : NaN);
    const score = Number.isFinite(at) && at > 0 ? at : Number(message.ts);
    if (!Number.isFinite(score) || score <= bestAt) continue;
    bestAt = score;
    best = message.ts || String(Math.floor(score));
  }
  return best;
}

function patchActivity(
  prev: Record<string, ChatActivitySnapshot>,
  accountId: string,
  channelId: string,
  patch: Partial<ChatActivity>,
): Record<string, ChatActivitySnapshot> {
  const snapshot = prev[accountId];
  if (!snapshot) return prev;
  const exists = snapshot.items.some((item) => item.id === channelId);
  const items = exists
    ? snapshot.items.map((item) => (item.id === channelId ? { ...item, ...patch } : item))
    : [...snapshot.items, { id: channelId, latestAt: null, readAt: null, unread: false, unreadCount: 0, ...patch }];
  return { ...prev, [accountId]: { ...snapshot, items } };
}

const CHAT_STATUS_CACHE_KEY = "chat:status";
const CHAT_CHANNELS_CACHE_KEY = "chat:channels";
const CHAT_ACTIVITY_CACHE_KEY = "chat:activity";
const CHAT_MESSAGES_CACHE_PREFIX = "chat:msgs:";
const CHAT_MESSAGES_CACHED = 200;
const CHAT_THREADS_CACHED = 80;
const CHAT_OPEN_THREAD_REFRESH_MS = 30_000;
const CHAT_LIST_REFRESH_MS = 5 * 60_000;

type ChatStatusSnapshot = { status: ChatStatus; accounts: ChatAccount[] };

/** Conversation keys only (no message text), newest first — feeds Home's "Jump back in". */
const RECENT_CHATS_KEY = "atw-hub-chat-recent";
const RECENT_CHATS_MAX = 12;
const HOME_JUMP_MIN = 6;

function readRecentChatKeys(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_CHATS_KEY) || "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string").slice(0, RECENT_CHATS_MAX) : [];
  } catch {
    return [];
  }
}

export function ChatPage() {
  useSlackEmoji();
  const searchQuery = useHubSearchQuery("chat").trim();
  const [status, setStatus] = useState<ChatStatus | null>(
    () => peekModuleCache<ChatStatusSnapshot>(CHAT_STATUS_CACHE_KEY)?.status ?? null,
  );
  const [accounts, setAccounts] = useState<ChatAccount[]>(
    () => peekModuleCache<ChatStatusSnapshot>(CHAT_STATUS_CACHE_KEY)?.accounts ?? [],
  );
  const [channelsByAccount, setChannelsByAccount] = useState<Record<string, ChatChannel[]>>(
    () => peekModuleCache<Record<string, ChatChannel[]>>(CHAT_CHANNELS_CACHE_KEY) ?? {},
  );
  const [channelErrors, setChannelErrors] = useState<Record<string, string>>({});
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [activityByAccount, setActivityByAccount] = useState<Record<string, ChatActivitySnapshot>>(
    () => peekModuleCache<Record<string, ChatActivitySnapshot>>(CHAT_ACTIVITY_CACHE_KEY) ?? {},
  );
  const [activityNonce, setActivityNonce] = useState(0);
  const [view, setView] = useState<ListView>({ scope: "home" });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [typingNames, setTypingNames] = useState<string[]>([]);
  const [menuMessage, setMenuMessage] = useState<ChatMessage | null>(null);
  /** Open message thread (Slack thread_ts) inside the conversation. */
  const [threadRoot, setThreadRoot] = useState<ChatMessage | null>(null);
  const [threadMessages, setThreadMessages] = useState<ChatMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState("");
  const threadRootRef = useRef<ChatMessage | null>(null);
  const pendingThreadRef = useRef<ChatMessage | null>(null);
  const [recentKeys, setRecentKeys] = useState<string[]>(readRecentChatKeys);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [treeExpanded, setTreeExpanded] = useState<Record<string, boolean>>({ [ALL_BRANCH]: true });
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return localStorage.getItem(NAV_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [sortField, setSortField] = useState<SortFieldId>("unread");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [filter, setFilter] = useState<FilterId>("all");
  const [sortOpen, setSortOpen] = useState(false);
  const [clockFormat, setClockFormat] = useState<MailClockFormatId>(() => readMailClockFormat());
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [checking, setChecking] = useState(false);
  const [ready, setReady] = useState(() => Boolean(peekModuleCache(CHAT_STATUS_CACHE_KEY)));
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [newDmOpen, setNewDmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ChatMessage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [threadInfo, setThreadInfo] = useState<ThreadInfo | null>(null);
  const [olderLoading, setOlderLoading] = useState(false);
  const [olderError, setOlderError] = useState("");
  const [rateLimit, setRateLimit] = useState<RateLimit | null>(null);
  const [clock, setClock] = useState(() => Date.now());
  const [retryNonce, setRetryNonce] = useState(0);
  const [needsYouNonce, setNeedsYouNonce] = useState(0);
  const [customerSetup, setCustomerSetup] = useState<SlackCustomerStatus | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const keepScrollRef = useRef<number | null>(null);
  const activeThreadRef = useRef<string | null>(null);
  const activityRef = useRef(activityByAccount);

  useEffect(() => {
    activityRef.current = activityByAccount;
  }, [activityByAccount]);

  const loadStatus = useCallback(async () => {
    try {
      const [nextStatus, nextAccounts] = await Promise.all([
        fetchChatStatus(),
        fetchChatAccounts().catch(() => [] as ChatAccount[]),
      ]);
      const mergedAccounts =
        nextAccounts.length > 0
          ? nextAccounts
          : (nextStatus.accounts || []).filter((a) => a.connected !== false);
      setStatus(nextStatus);
      // Same accounts as the saved copy: keep identity so channels don't reload twice.
      setAccounts((prev) =>
        JSON.stringify(prev) === JSON.stringify(mergedAccounts) ? prev : mergedAccounts,
      );
      setError("");
      void writeModuleCache<ChatStatusSnapshot>(
        CHAT_STATUS_CACHE_KEY,
        { status: nextStatus, accounts: mergedAccounts },
        { mirror: true },
      );
      return { status: nextStatus, accounts: mergedAccounts };
    } catch (err) {
      // Offline or a transient failure: keep the saved accounts on screen.
      if (peekModuleCache(CHAT_STATUS_CACHE_KEY)) return null;
      setStatus({ connected: false, configured: false });
      setAccounts([]);
      setError(err instanceof Error ? err.message : "Could not load chat status.");
      return null;
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const anyConnected = accounts.length > 0 || Boolean(status?.connected);

  const loadChannels = useCallback(async () => {
    if (!accounts.length) {
      setChannelsByAccount({});
      return;
    }
    const saved = peekModuleCache<Record<string, ChatChannel[]>>(CHAT_CHANNELS_CACHE_KEY) || {};
    // Saved channels are already on screen; refresh them without the loading state.
    if (!accounts.some((a) => saved[a.id]?.length)) setChannelsLoading(true);
    const results = await Promise.allSettled(accounts.map((a) => fetchChatChannels(a.id)));
    const nextChannels: Record<string, ChatChannel[]> = {};
    const nextErrors: Record<string, string> = {};
    results.forEach((result, i) => {
      const account = accounts[i]!;
      if (result.status === "fulfilled") {
        nextChannels[account.id] = result.value.filter(
          (ch) => !ch.accountId || ch.accountId === account.id,
        );
      } else {
        nextChannels[account.id] = saved[account.id] || [];
        nextErrors[account.id] =
          result.reason instanceof Error ? result.reason.message : "Chat unavailable.";
      }
    });
    setChannelsByAccount(nextChannels);
    setChannelErrors(nextErrors);
    setChannelsLoading(false);
    void writeModuleCache(CHAT_CHANNELS_CACHE_KEY, nextChannels, { mirror: true });
  }, [accounts]);

  useEffect(() => {
    void loadChannels();
  }, [loadChannels]);

  const slackAccounts = useMemo(() => accounts.filter((a) => providerOf(a) === "slack"), [accounts]);
  const hasSlack = slackAccounts.length > 0;
  // Customer-mode accounts load live only when opened: no background activity checks for them.
  const activityAccounts = useMemo(
    () =>
      accounts.filter(
        (a) =>
          a.providerId === "zoom" ||
          a.providerId === "teams" ||
          (a.providerId === "slack" && !isSlackCustomerAccount(a)),
      ),
    [accounts],
  );
  const customerAccounts = useMemo(() => accounts.filter(isSlackCustomerAccount), [accounts]);
  const activityKey = activityAccounts.map((a) => a.id).join(",");

  // Activity drives Home, Unread, and Recent; poll quickly while the API is still checking.
  useEffect(() => {
    const ids = activityKey ? activityKey.split(",") : [];
    if (!ids.length) return;
    let cancelled = false;
    let timer: number | undefined;
    async function poll() {
      const results = await Promise.allSettled(ids.map((id) => fetchChatActivity(id)));
      if (cancelled) return;
      setActivityByAccount((prev) => {
        const next = { ...prev };
        results.forEach((result, i) => {
          if (result.status === "fulfilled") next[ids[i]!] = result.value;
        });
        for (const id of Object.keys(next)) if (!ids.includes(id)) delete next[id];
        void writeModuleCache(CHAT_ACTIVITY_CACHE_KEY, next, { mirror: true });
        return next;
      });
      const pending = results.some((r) => r.status === "fulfilled" && !r.value.complete);
      timer = window.setTimeout(() => void poll(), pending ? 4000 : 60000);
    }
    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [activityKey, activityNonce]);

  const needsYou = useNeedsYou(customerAccounts, view.scope === "home", needsYouNonce);
  const pilotCustomers = customerAccounts.filter((a) => !isListedSlackCustomer(a));

  // Only people without any chat account see the setup screen; offer the ATW Hub app there too.
  useEffect(() => {
    if (!ready || accounts.length || status?.connected) return;
    let cancelled = false;
    void fetchSlackCustomerStatus()
      .then((next) => {
        if (!cancelled) setCustomerSetup(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [ready, accounts.length, status?.connected]);

  useEffect(() => {
    function syncClock() {
      setClockFormat(readMailClockFormat());
    }
    window.addEventListener(MAIL_CLOCK_FORMAT_EVENT, syncClock);
    return () => window.removeEventListener(MAIL_CLOCK_FORMAT_EVENT, syncClock);
  }, []);

  useEffect(() => {
    if (!sortOpen) return;
    function onDoc(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Element && target.closest(".mail-sort-wrap")) return;
      setSortOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sortOpen]);

  // Every account branch starts expanded; drop a folder view whose account went away.
  useEffect(() => {
    setTreeExpanded((prev) => {
      const next = { ...prev };
      for (const a of accounts) if (next[a.id] === undefined) next[a.id] = true;
      return next;
    });
    setView((prev) =>
      prev.scope === "account" && !accounts.some((a) => a.id === prev.accountId) ? { scope: "home" } : prev,
    );
  }, [accounts]);

  useEffect(() => {
    function onNew() {
      if (!hasSlack) {
        setNotice("New Chat — connect Slack in Settings to start a direct message.");
        return;
      }
      setChatDrawerOpen(false);
      setNewDmOpen(true);
    }
    window.addEventListener("atw-hub-new-chat", onNew);
    return () => window.removeEventListener("atw-hub-new-chat", onNew);
  }, [hasSlack]);

  useEffect(() => {
    function openDrawer() {
      setChatDrawerOpen(true);
    }
    window.addEventListener("atw-hub-open-chat-drawer", openDrawer);
    return () => window.removeEventListener("atw-hub-open-chat-drawer", openDrawer);
  }, []);

  // Same FAB hide as Mail while a conversation is open (phone reader sits under the FAB).
  useEffect(() => {
    if (selectedKey) document.documentElement.dataset.mailReader = "1";
    else delete document.documentElement.dataset.mailReader;
    return () => {
      delete document.documentElement.dataset.mailReader;
    };
  }, [selectedKey]);

  const accountById = useMemo(() => {
    const map = new Map<string, ChatAccount>();
    for (const a of accounts) map.set(a.id, a);
    return map;
  }, [accounts]);

  // A workspace can be connected through both the test app and the ATW Hub app; tell them apart.
  const hubAppTwins = useMemo(() => {
    const testTeams = new Set(activityAccounts.map((a) => a.teamId).filter(Boolean));
    return new Set(customerAccounts.filter((a) => a.teamId && testTeams.has(a.teamId)).map((a) => a.id));
  }, [activityAccounts, customerAccounts]);
  const accountLabel = useCallback(
    (account: ChatAccount) =>
      hubAppTwins.has(account.id) ? `${chatAccountLabel(account)} (ATW Hub app)` : chatAccountLabel(account),
    [hubAppTwins],
  );

  const allRows = useMemo(() => {
    const rows: ChatRow[] = [];
    for (const account of accounts) {
      const providerId = providerOf(account);
      const snapshot = activityByAccount[account.id];
      const activity = new Map((snapshot?.items || []).map((item) => [item.id, item]));
      for (const channel of channelsByAccount[account.id] || []) {
        const folder = chatFolderForChannel(providerId, channel);
        const type = chatType(folder);
        const item = activity.get(channel.id);
        const title = conversationTitle(channel, type, snapshot?.selfHandle);
        rows.push({
          key: channelKey(account.id, channel.id),
          account,
          providerId,
          channel,
          folder,
          type,
          title,
          sortName: title.replace(/^#/, "").toLowerCase(),
          activity: item,
          at: item?.latestAt ?? null,
          orderAt: item?.latestAt ?? item?.readAt ?? null,
          unread: Boolean(item?.unread),
          unreadCount: item?.unread ? Math.max(0, Number(item.unreadCount) || 0) : 0,
          preview: previewFor(type, channel, item),
        });
      }
    }
    return rows;
  }, [accounts, channelsByAccount, activityByAccount]);

  const rowByKey = useMemo(() => new Map(allRows.map((row) => [row.key, row])), [allRows]);

  const needsRows = useMemo((): NeedsRow[] => {
    const out: NeedsRow[] = [];
    for (const account of customerAccounts) {
      for (const item of needsYou.snapshots[account.id]?.items || []) {
        const row = rowByKey.get(channelKey(account.id, item.channelId));
        const direct = row ? row.type === "direct" : item.kind === "dm";
        out.push({
          key: `${account.id}:${item.channelId}:${item.ts}`,
          account,
          item,
          row,
          title:
            row?.title ||
            (item.kind === "dm" ? item.author : item.channelName ? `#${item.channelName}` : "Slack"),
          preview: direct ? item.text : `${item.author}: ${item.text}`,
        });
      }
    }
    return out
      .filter((row) => !searchQuery || matchesHubSearch(searchQuery, row.title, row.preview, row.item.author))
      .sort((a, b) => (b.item.at ?? 0) - (a.item.at ?? 0));
  }, [customerAccounts, needsYou.snapshots, rowByKey, searchQuery]);

  // Conversations per folder, and unread messages per folder (an uncounted unread chat counts as 1).
  const counts = useMemo(() => {
    const total = new Map<string, number>();
    const unread = new Map<string, number>();
    const add = (map: Map<string, number>, key: string, n: number) => map.set(key, (map.get(key) || 0) + n);
    for (const row of allRows) {
      const keys = [
        `${ALL_BRANCH}:everything`,
        `${ALL_BRANCH}:${row.type}`,
        `${row.account.id}:${ALL_MESSAGES}`,
        `${row.account.id}:${row.folder}`,
      ];
      const unreadMessages = row.unread ? Math.max(1, row.unreadCount) : 0;
      for (const key of keys) {
        add(total, key, 1);
        if (unreadMessages) add(unread, key, unreadMessages);
      }
    }
    return { total, unread };
  }, [allRows]);

  const sections = useMemo((): ListSection[] => {
    const now = Date.now();
    const passes = (row: ChatRow) =>
      filter === "unread" ? row.unread : filter === "week" ? row.at != null && now - row.at < WEEK_MS : true;
    const sorted = (rows: ChatRow[]) => rows.sort((a, b) => compareRows(a, b, sortField, sortDir));
    if (searchQuery) {
      const matches = allRows.filter(
        (row) =>
          passes(row) &&
          (view.scope === "home" ||
            (view.scope === "all"
              ? view.folder === "everything" || row.type === view.folder
              : row.account.id === view.accountId && (view.folder === ALL_MESSAGES || row.folder === view.folder))) &&
          chatRowMatchesSearch(row, searchQuery),
      );
      return [{ id: "search", label: "Results", rows: sorted(matches) }];
    }
    if (view.scope === "home") {
      // To review: anything unread, plus conversations where someone else wrote this week.
      const review = allRows.filter(
        (row) =>
          passes(row) &&
          (row.unread || (row.at != null && now - row.at < WEEK_MS && row.activity?.latestAuthor !== "You")),
      );
      // Jump back in: conversations opened lately, topped up with the most recent DMs.
      const shown = new Set(review.map((row) => row.key));
      const jump: ChatRow[] = [];
      for (const key of recentKeys) {
        const row = rowByKey.get(key);
        if (row && !shown.has(row.key) && filter === "all") {
          jump.push(row);
          shown.add(row.key);
        }
      }
      if (filter === "all" && jump.length < HOME_JUMP_MIN) {
        const dms = allRows
          .filter((row) => row.type !== "channels" && !shown.has(row.key))
          .sort((a, b) => compareRows(a, b, "recent", "desc"));
        jump.push(...dms.slice(0, HOME_JUMP_MIN - jump.length));
      }
      return [
        { id: "unread", label: "Unread", rows: sorted(review.filter((row) => row.unread)) },
        { id: "recent", label: "This week", rows: sorted(review.filter((row) => !row.unread)) },
        { id: "jump", label: "Jump back in", rows: jump },
      ];
    }
    const scoped = allRows.filter((row) =>
      view.scope === "all"
        ? view.folder === "everything" || row.type === view.folder
        : row.account.id === view.accountId && (view.folder === ALL_MESSAGES || row.folder === view.folder),
    );
    return [{ id: "list", label: "", rows: sorted(scoped.filter(passes)) }];
  }, [allRows, rowByKey, recentKeys, view, filter, sortField, sortDir, searchQuery]);

  const listedCount = sections.reduce((sum, section) => sum + section.rows.length, 0);
  const selectedRow = selectedKey ? rowByKey.get(selectedKey) : undefined;
  const threadProvider = selectedRow?.providerId ?? "slack";
  const caps = chatCapabilities(selectedRow?.account);

  const markThreadRead = useCallback((accountId: string, channelId: string, loaded: ChatMessage[]) => {
    const item = activityRef.current[accountId]?.items.find((entry) => entry.id === channelId);
    if (!item?.unread) return;
    const ts = item.latestTs || newestTs(loaded);
    if (!ts) return;
    setActivityByAccount((prev) =>
      patchActivity(prev, accountId, channelId, { unread: false, unreadCount: 0, readAt: Date.now() }),
    );
    void markChatRead(accountId, channelId, ts).catch(() => {});
  }, []);

  const loadMessages = useCallback(
    async (
      accountId: string,
      channelId: string,
      opts?: { quiet?: boolean; customer?: boolean; markRead?: boolean },
    ) => {
      const key = channelKey(accountId, channelId);
      activeThreadRef.current = key;
      // Customer-mode Slack (ATW Hub app) must not keep message text on the device.
      const cacheKey = opts?.customer ? null : CHAT_MESSAGES_CACHE_PREFIX + key;
      let hasSaved = false;
      if (!opts?.quiet) {
        setMessagesError("");
        setRateLimit(null);
        const saved = cacheKey ? await readModuleCache<ChatMessage[]>(cacheKey) : null;
        if (activeThreadRef.current !== key) return;
        hasSaved = Boolean(saved?.length);
        if (saved?.length) setMessages(saved);
        setMessagesLoading(!hasSaved);
      }
      try {
        if (opts?.customer) {
          const page = await fetchSlackCustomerMessages(accountId, channelId);
          if (activeThreadRef.current !== key) return;
          setMessages(page.messages);
          setThreadInfo({
            key,
            hasMore: page.hasMore,
            nextCursor: page.nextCursor,
            limited: page.limited,
            permalink: page.permalink,
          });
          const ts = opts.markRead && !opts.quiet ? newestTs(page.messages) : undefined;
          if (ts) void markChatRead(accountId, channelId, ts).catch(() => {});
          return;
        }
        const next = await fetchChatThread(accountId, channelId);
        if (cacheKey) {
          void writeModuleCache(cacheKey, next.messages.slice(-CHAT_MESSAGES_CACHED)).then(() =>
            pruneModuleCache(CHAT_MESSAGES_CACHE_PREFIX, CHAT_THREADS_CACHED),
          );
        }
        if (activeThreadRef.current === key) {
          setMessages(next.messages);
          setTypingNames(next.typing);
          if (!opts?.quiet) markThreadRead(accountId, channelId, next.messages);
        }
      } catch (err) {
        if (activeThreadRef.current !== key || opts?.quiet || hasSaved) return;
        setMessages([]);
        setTypingNames([]);
        if (opts?.customer) {
          const permalink = slackErrorPermalink(err);
          setThreadInfo({ key, hasMore: false, limited: false, permalink });
          const wait = slackRetryAfter(err);
          if (wait != null) {
            setRateLimit({ key, until: Date.now() + wait * 1000, permalink });
            return;
          }
        }
        if (err instanceof ApiError && err.status === 404) {
          setMessagesError("The Hub API does not return messages for this conversation yet.");
        } else {
          setMessagesError(err instanceof Error ? err.message : "Could not load messages.");
        }
      } finally {
        if (!opts?.quiet && activeThreadRef.current === key) setMessagesLoading(false);
      }
    },
    [markThreadRead],
  );

  function patchMessage(id: string, patch: (message: ChatMessage) => ChatMessage) {
    setMessages((prev) => prev.map((m) => (m.id === id ? patch(m) : m)));
    setThreadMessages((prev) => prev.map((m) => (m.id === id ? patch(m) : m)));
    setThreadRoot((prev) => (prev && prev.id === id ? patch(prev) : prev));
  }

  async function toggleReaction(message: ChatMessage, name: string) {
    setMenuMessage(null);
    if (!selectedRow || !message.ts || !caps.reactions) return;
    const accountId = selectedRow.account.id;
    const channelId = selectedRow.channel.id;
    const on = !message.reactions?.some((item) => item.name === name && item.mine);
    patchMessage(message.id, (m) => withReaction(m, name, on));
    try {
      await setChatReaction(accountId, channelId, message.ts, name, on);
    } catch (err) {
      patchMessage(message.id, (m) => withReaction(m, name, !on));
      setNotice(chatActionError(err, on ? "Could not add the reaction." : "Could not remove the reaction."));
    }
  }

  async function loadThreadReplies(root: ChatMessage, opts?: { quiet?: boolean }) {
    const row = selectedRow;
    const threadTs = root.threadTs || root.ts;
    if (!row || !threadTs) return;
    if (!opts?.quiet) {
      setThreadLoading(true);
      setThreadError("");
    }
    try {
      const { messages: rows } = await fetchChatThreadReplies(row.account.id, row.channel.id, threadTs);
      if (threadRootRef.current?.id !== root.id) return;
      const parent = rows.find((m) => m.ts === threadTs);
      if (parent) setThreadRoot((prev) => (prev ? { ...prev, ...parent, id: prev.id } : prev));
      setThreadMessages(rows.filter((m) => m.ts !== threadTs));
      setThreadError("");
    } catch (err) {
      if (threadRootRef.current?.id !== root.id) return;
      const wait = slackRetryAfter(err);
      setThreadError(
        wait != null
          ? `Slack is limiting how often ATW Hub can load threads. Try again in ${wait} seconds.`
          : chatActionError(err, "Could not load this thread."),
      );
    } finally {
      if (!opts?.quiet && threadRootRef.current?.id === root.id) setThreadLoading(false);
    }
  }

  function openInProvider(message: ChatMessage) {
    setMenuMessage(null);
    if (!selectedRow || !message.ts) return;
    const accountId = selectedRow.account.id;
    const channelId = selectedRow.channel.id;
    const ts = message.ts;
    void openFetchedLink(() => fetchChatPermalink(accountId, channelId, ts)).then((ok) => {
      if (!ok) setNotice(`Could not open this message in ${providerName(threadProvider)}.`);
    });
  }

  function openThread(message: ChatMessage) {
    setMenuMessage(null);
    if (!caps.threads) {
      if (caps.permalink) openInProvider(message);
      return;
    }
    threadRootRef.current = message;
    setThreadRoot(message);
    setThreadMessages([]);
    void loadThreadReplies(message);
  }

  function closeThread() {
    threadRootRef.current = null;
    setThreadRoot(null);
    setThreadMessages([]);
    setThreadError("");
    setThreadLoading(false);
  }

  async function handleThreadReply(input: ChatSendInput) {
    const root = threadRoot;
    const threadTs = root?.threadTs || root?.ts;
    if (!root || !threadTs || !selectedRow) return;
    const sent = await sendChatMessage(selectedRow.account.id, selectedRow.channel.id, { ...input, threadTs });
    if (threadRootRef.current?.id !== root.id) return;
    if (sent) {
      const mine = { ...sent, self: true, parentTs: threadTs };
      setThreadMessages((prev) => (prev.some((m) => m.id === mine.id) ? prev : [...prev, mine]));
    }
    patchMessage(root.id, (m) => ({ ...m, threadTs, replyCount: (m.replyCount || 0) + 1 }));
    void loadThreadReplies(root, { quiet: true });
  }

  async function handleSend(input: ChatSendInput) {
    if (!selectedRow) return;
    const accountId = selectedRow.account.id;
    const channelId = selectedRow.channel.id;
    const sent = await sendChatMessage(accountId, channelId, input);
    if (sent && activeThreadRef.current === channelKey(accountId, channelId)) {
      // Our own POST response — the connected account is the sender by definition.
      const mine = { ...sent, self: true };
      setMessages((prev) => (prev.some((m) => m.id === mine.id) ? prev : [...prev, mine]));
    }
    if (sent) {
      setActivityByAccount((prev) =>
        patchActivity(prev, accountId, channelId, {
          latestAt: sent.at ?? Date.now(),
          latestTs: sent.ts,
          latestAuthor: "You",
          latestText: sent.text || input.body,
          unread: false,
          unreadCount: 0,
        }),
      );
    }
    // Customer mode keeps the POST response: a reload would spend Slack's history rate limit.
    if (!isSlackCustomerAccount(selectedRow.account)) void loadMessages(accountId, channelId, { quiet: true });
  }

  async function loadOlderMessages() {
    const info = threadInfo;
    if (!selectedRow || !info?.nextCursor || info.key !== selectedKey) return;
    const accountId = selectedRow.account.id;
    const channelId = selectedRow.channel.id;
    setOlderLoading(true);
    setOlderError("");
    try {
      const page = await fetchSlackCustomerMessages(accountId, channelId, info.nextCursor);
      if (activeThreadRef.current !== info.key) return;
      const el = messagesRef.current;
      keepScrollRef.current = el ? el.scrollHeight - el.scrollTop : null;
      setMessages((prev) => {
        const have = new Set(prev.map((m) => m.id));
        return [...page.messages.filter((m) => !have.has(m.id)), ...prev];
      });
      setThreadInfo({
        key: info.key,
        hasMore: page.hasMore,
        nextCursor: page.nextCursor,
        limited: page.limited,
        permalink: page.permalink || info.permalink,
      });
    } catch (err) {
      const wait = slackRetryAfter(err);
      setOlderError(
        wait != null
          ? `Slack is limiting how often ATW Hub can load messages. Try again in ${wait} seconds.`
          : chatActionError(err, "Could not load older messages."),
      );
    } finally {
      setOlderLoading(false);
    }
  }

  async function handleEditMessage(message: ChatMessage, text: string) {
    if (!selectedRow || !message.ts) return;
    const accountId = selectedRow.account.id;
    const channelId = selectedRow.channel.id;
    const updated = await editChatMessage(accountId, channelId, message.ts, text);
    if (activeThreadRef.current === channelKey(accountId, channelId)) {
      patchMessage(message.id, (m) => ({ ...m, text: updated?.text || text, self: true, edited: true }));
    }
  }

  function requestDeleteMessage(message: ChatMessage) {
    setDeleteError("");
    setPendingDelete(message);
  }

  async function confirmDeleteMessage() {
    const target = pendingDelete;
    if (!target?.ts || !selectedRow) return;
    const accountId = selectedRow.account.id;
    const channelId = selectedRow.channel.id;
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteChatMessage(accountId, channelId, target.ts);
      setMessages((prev) => prev.filter((m) => m.id !== target.id));
      setThreadMessages((prev) => prev.filter((m) => m.id !== target.id));
      if (threadRootRef.current?.id === target.id) closeThread();
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(chatActionError(err, "Could not delete the message."));
    } finally {
      setDeleting(false);
    }
  }

  function handleDmOpened(accountId: string, channel: ChatChannel) {
    const folder = chatFolderForChannel(providerOf(accountById.get(accountId)), channel);
    setChannelsByAccount((prev) => {
      const list = prev[accountId] || [];
      return list.some((ch) => ch.id === channel.id)
        ? prev
        : { ...prev, [accountId]: [{ ...channel, accountId }, ...list] };
    });
    setView({ scope: "account", accountId, folder });
    setSelectedKey(channelKey(accountId, channel.id));
    setNewDmOpen(false);
  }

  useEffect(() => {
    setPendingDelete(null);
    setDeleteError("");
    setOlderError("");
    setMenuMessage(null);
    if (selectedKey) {
      setRecentKeys((prev) => {
        const next = [selectedKey, ...prev.filter((k) => k !== selectedKey)].slice(0, RECENT_CHATS_MAX);
        try {
          localStorage.setItem(RECENT_CHATS_KEY, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    }
    const pending = pendingThreadRef.current;
    pendingThreadRef.current = null;
    if (pending) openThread(pending);
    else closeThread();
    // openThread reads the newly selected row; only the key change should trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  useEffect(() => {
    if (!selectedKey || !selectedRow) {
      activeThreadRef.current = null;
      setMessages([]);
      setTypingNames([]);
      setMessagesError("");
      setRateLimit(null);
      return;
    }
    void loadMessages(selectedRow.account.id, selectedRow.channel.id, threadLoadOptions(selectedRow));
    // selectedRow identity changes on every channels/activity refresh; key on the id only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, loadMessages, retryNonce]);

  // Customer-mode conversations stay live-on-open only (Slack history rate limits).
  useLiveRefresh(() => {
    if (!selectedRow || isSlackCustomerAccount(selectedRow.account)) return;
    void loadMessages(selectedRow.account.id, selectedRow.channel.id, { quiet: true });
    if (threadRootRef.current) void loadThreadReplies(threadRootRef.current, { quiet: true });
  }, CHAT_OPEN_THREAD_REFRESH_MS);

  useLiveRefresh(() => {
    void loadChannels();
    setActivityNonce((n) => n + 1);
  }, CHAT_LIST_REFRESH_MS);

  // Count down Slack's rate limit on the open conversation, then load it again.
  useEffect(() => {
    if (!rateLimit) return;
    setClock(Date.now());
    const tick = window.setInterval(() => setClock(Date.now()), 1000);
    const retry = window.setTimeout(
      () => {
        setRateLimit(null);
        setRetryNonce((n) => n + 1);
      },
      Math.max(0, rateLimit.until - Date.now()),
    );
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(retry);
    };
  }, [rateLimit]);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    // Older messages were added above: keep the reader where it was.
    const fromBottom = keepScrollRef.current;
    keepScrollRef.current = null;
    el.scrollTop = fromBottom == null ? el.scrollHeight : el.scrollHeight - fromBottom;
  }, [messages]);

  // Hub Home's Needs you links here as /chat?open=<accountId>:<channelId>.
  const openParam = searchParams.get("open") || "";
  useEffect(() => {
    if (!openParam || channelsLoading || !Object.keys(channelsByAccount).length) return;
    if (rowByKey.has(openParam)) setSelectedKey(openParam);
    else setNotice("That conversation isn't in your chat list. Open it in Slack instead.");
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("open");
        return next;
      },
      { replace: true },
    );
  }, [openParam, channelsLoading, channelsByAccount, rowByKey, setSearchParams]);

  function toggleTreeBranch(id: string) {
    setTreeExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function selectView(next: ListView) {
    setView(next);
    setSelectedKey(null);
    setChatDrawerOpen(false);
  }

  function selectAllChats() {
    if (view.scope === "all" && view.folder === "everything") {
      toggleTreeBranch(ALL_BRANCH);
      return;
    }
    setTreeExpanded((prev) => ({ ...prev, [ALL_BRANCH]: true }));
    selectView({ scope: "all", folder: "everything" });
  }

  function selectSortField(field: SortFieldId) {
    if (field === sortField) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      return;
    }
    setSortField(field);
    // Unread first and Recent start newest-first; Name starts A→Z.
    setSortDir(field === "name" ? "asc" : "desc");
  }

  function refreshAll() {
    void loadChannels();
    setActivityNonce((n) => n + 1);
    if (view.scope === "home") setNeedsYouNonce((n) => n + 1);
    if (selectedRow) {
      void loadMessages(selectedRow.account.id, selectedRow.channel.id, threadLoadOptions(selectedRow));
    }
  }

  function openNeedsRow(needs: NeedsRow) {
    if (needs.row && !needs.item.threadTs) {
      setSelectedKey(needs.row.key);
      return;
    }
    // Thread replies open in Hub's thread view when the account supports it, otherwise in Slack.
    if (needs.row && needs.item.threadTs && chatCapabilities(needs.row.account).threads) {
      const threadTs = needs.item.threadTs;
      const root: ChatMessage = { id: `thread-${threadTs}`, ts: threadTs, threadTs, author: "", text: "", at: null };
      if (needs.row.key === selectedKey) openThread(root);
      else {
        pendingThreadRef.current = root;
        setSelectedKey(needs.row.key);
      }
      return;
    }
    if (needs.item.permalink) openHrefInNewTab(needs.item.permalink);
    else if (needs.row) setSelectedKey(needs.row.key);
  }

  function openNewChat() {
    setChatDrawerOpen(false);
    setNewDmOpen(true);
  }

  function toggleNav() {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  async function connectProvider(providerId: ChatProviderId) {
    setChecking(true);
    setNotice("");
    setError("");
    try {
      const result = await startChatConnect(providerId, { forceLogin: true }).catch(() => null);
      const url = result ? connectUrl(result) : undefined;
      if (url) {
        openHrefInNewTab(url);
        const opened =
          providerId === "cliq"
            ? "Zoho Cliq"
            : providerId === "zoom"
              ? "Zoom Team Chat"
              : providerId === "teams"
                ? "Microsoft Teams"
                : "Slack";
        setNotice(`Opened ${opened} authorization. After consent, you can Connect again for another account.`);
        return;
      }
      const next = await loadStatus();
      if (next?.accounts.length) {
        setNotice("Chat account connected.");
      } else if (providerConfigured(next?.status ?? status, providerId)) {
        setNotice(
          `${providerName(providerId)} credentials are on the API host. Tap Connect in Settings to finish OAuth.`,
        );
      } else {
        setNotice(
          `${providerName(providerId)} is not configured on the API host yet. Open Settings for setup steps.`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start chat connect.");
    } finally {
      setChecking(false);
    }
  }

  async function checkSetup() {
    await connectProvider("slack");
  }

  const slackReady = providerConfigured(status, "slack");
  const cliqReady = providerConfigured(status, "cliq");
  const showSetup = !anyConnected;

  const setupSteps =
    status?.setupSteps && status.setupSteps.length > 0
      ? status.setupSteps
      : DEFAULT_SLACK_SETUP_STEPS;

  const viewAccount = view.scope === "account" ? accountById.get(view.accountId) : undefined;
  const viewFolderLabel =
    view.scope === "account"
      ? view.folder === ALL_MESSAGES
        ? ALL_MESSAGES_LABEL
        : CHAT_FOLDERS[providerOf(viewAccount)].find((f) => f.id === view.folder)?.label || "Chats"
      : view.scope === "all"
        ? view.folder === "everything"
          ? ALL_MESSAGES_LABEL
          : typeLabel(view.folder)
        : "";
  const viewLabel =
    view.scope === "home"
      ? "Home"
      : view.scope === "all"
        ? viewFolderLabel
          ? `All Chats · ${viewFolderLabel}`
          : "All Chats"
        : `${providerName(providerOf(viewAccount))} · ${viewAccount ? accountLabel(viewAccount) : "Chat"} · ${viewFolderLabel}`;
  const backLabel = view.scope === "account" ? viewFolderLabel.toLowerCase() : view.scope === "home" ? "Home" : "All Chats";
  const showAccountChip = view.scope !== "account" && accounts.length > 1;

  // Only the first check is worth announcing; later refreshes happen quietly in the background.
  const checkingAccounts = activityAccounts.filter((a) => {
    const snapshot = activityByAccount[a.id];
    return snapshot && !snapshot.complete && !snapshot.refreshedAt;
  });
  const limitedAccounts = activityAccounts.filter((a) => activityByAccount[a.id]?.mode === "limited");
  const listErrors =
    view.scope === "account"
      ? channelErrors[view.accountId]
        ? [channelErrors[view.accountId]!]
        : []
      : Object.entries(channelErrors).map(([id, message]) => {
          const account = accountById.get(id);
          return account ? `${accountLabel(account)}: ${message}` : message;
        });
  const needsYouErrors =
    view.scope === "home"
      ? Object.entries(needsYou.errors).map(([id, message]) => {
          const account = accountById.get(id);
          return account && customerAccounts.length > 1 ? `${accountLabel(account)}: ${message}` : message;
        })
      : [];
  const needsYouPartial =
    view.scope === "home"
      ? customerAccounts.map((a) => needsYou.snapshots[a.id]?.partialError).filter((m): m is string => Boolean(m))
      : [];
  const homeEmptyText = checkingAccounts.length
    ? "Checking your chats for anything new…"
    : activityAccounts.length || !customerAccounts.length
      ? "You're all caught up — nothing new this week."
      : needsYou.loading
        ? "Checking Slack for anything that needs you…"
        : pilotCustomers.length === customerAccounts.length
          ? "Open All Chats to see your Slack conversations."
          : "Nothing in Slack needs you right now.";
  const homeNeedsCount = view.scope === "home" ? needsRows.length : 0;
  const rateLimitSeconds =
    rateLimit && rateLimit.key === selectedKey ? Math.max(1, Math.ceil((rateLimit.until - clock) / 1000)) : 0;
  const customerThread =
    selectedRow && isSlackCustomerAccount(selectedRow.account) && threadInfo?.key === selectedKey
      ? threadInfo
      : null;

  const newDmAccountId =
    (viewAccount && providerOf(viewAccount) === "slack" ? viewAccount.id : undefined) ||
    (selectedRow?.providerId === "slack" ? selectedRow.account.id : undefined) ||
    slackAccounts[0]?.id ||
    "";

  /** Unread messages as a bubble; otherwise how many conversations the folder holds. */
  function renderFolderCount(countKey: string, opts?: { unreadOnly?: boolean }): ReactNode {
    const unread = counts.unread.get(countKey) || 0;
    if (unread) {
      return (
        <small className="chat-unread-badge" aria-label={unreadLabel(unread)}>
          {unreadBadgeText(unread)}
        </small>
      );
    }
    const total = opts?.unreadOnly ? 0 : counts.total.get(countKey) || 0;
    return total ? <small className="chat-tree-count">{total}</small> : null;
  }

  function renderTree(): ReactNode {
    const allOpen = Boolean(treeExpanded[ALL_BRANCH]);
    return (
      <div className="mail-tree" aria-label="Chat accounts">
        <div className="mail-tree-branch">
          <button
            type="button"
            className={
              view.scope === "all" && view.folder === "everything"
                ? "mail-tree-account active"
                : "mail-tree-account"
            }
            aria-expanded={allOpen}
            onClick={selectAllChats}
          >
            <span className="mail-tree-caret" aria-hidden>
              {allOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
            <span className="mail-tree-stack">
              <strong>All Chats</strong>
              <small>
                {accounts.length} account{accounts.length === 1 ? "" : "s"}
              </small>
            </span>
          </button>
          {allOpen ? (
            <div className="mail-tree-folders">
              <button
                type="button"
                className={
                  view.scope === "home"
                    ? "mail-tree-folder active chat-tree-folder"
                    : "mail-tree-folder chat-tree-folder"
                }
                onClick={() => selectView({ scope: "home" })}
              >
                <span>Home</span>
                {renderFolderCount(`${ALL_BRANCH}:everything`, { unreadOnly: true })}
              </button>
              <button
                type="button"
                className={
                  view.scope === "all" && view.folder === "everything"
                    ? "mail-tree-folder active chat-tree-folder"
                    : "mail-tree-folder chat-tree-folder"
                }
                onClick={() => selectView({ scope: "all", folder: "everything" })}
              >
                <span>{ALL_MESSAGES_LABEL}</span>
                {renderFolderCount(`${ALL_BRANCH}:everything`)}
              </button>
              {ALL_FOLDERS.map((item) => (
                <button
                  key={`all-${item.id}`}
                  type="button"
                  className={
                    view.scope === "all" && view.folder === item.id
                      ? "mail-tree-folder active chat-tree-folder"
                      : "mail-tree-folder chat-tree-folder"
                  }
                  onClick={() => selectView({ scope: "all", folder: item.id })}
                >
                  <span>{item.label}</span>
                  {renderFolderCount(`${ALL_BRANCH}:${item.id}`)}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {accounts.map((account) => {
          const open = Boolean(treeExpanded[account.id]);
          const providerId = providerOf(account);
          return (
            <div key={account.id} className="mail-tree-branch">
              <button
                type="button"
                className={
                  view.scope === "account" && view.accountId === account.id
                    ? "mail-tree-account active"
                    : "mail-tree-account"
                }
                title={account.email || accountLabel(account)}
                aria-expanded={open}
                onClick={() => toggleTreeBranch(account.id)}
              >
                <span className="mail-tree-caret" aria-hidden>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <span className="mail-tree-stack">
                  <strong>{providerName(providerId)}</strong>
                  <small className="mail-email-truncate">{accountLabel(account)}</small>
                </span>
              </button>
              {open ? (
                <div className="mail-tree-folders">
                  {[{ id: ALL_MESSAGES, label: ALL_MESSAGES_LABEL } as const, ...CHAT_FOLDERS[providerId]].map((item) => (
                    <button
                      key={`${account.id}-${item.id}`}
                      type="button"
                      className={
                        view.scope === "account" && view.accountId === account.id && view.folder === item.id
                          ? "mail-tree-folder active chat-tree-folder"
                          : "mail-tree-folder chat-tree-folder"
                      }
                      onClick={() => selectView({ scope: "account", accountId: account.id, folder: item.id })}
                    >
                      <span>{item.label}</span>
                      {renderFolderCount(`${account.id}:${item.id}`)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  function renderRow(row: ChatRow): ReactNode {
    const when = formatListWhen(row.at, clockFormat);
    const active = selectedKey === row.key;
    return (
      <li key={row.key}>
        <button
          type="button"
          className={`mail-row chat-row${active ? " active" : ""}${row.unread ? " is-unread" : ""}`}
          aria-current={active ? "true" : undefined}
          onClick={() => setSelectedKey(row.key)}
        >
          <span className="chat-row-main">
            <ChatListFace
              type={row.type}
              title={row.title}
              avatarUrl={row.channel.avatarUrl}
              faceKey={row.channel.id || row.key}
            />
            <span className="chat-row-body">
              {showAccountChip ? (
                <span className="mail-row-account">
                  {providerName(row.providerId)} · {accountLabel(row.account)}
                </span>
              ) : null}
              <div className="mail-row-top">
                <span className="mail-row-from-wrap">
                  <strong className={row.unread ? "unread" : undefined}>{row.title}</strong>
                </span>
                {when ? (
                  <div className="mail-row-indicators">
                    <span className="mail-row-time">{when}</span>
                  </div>
                ) : null}
              </div>
              {row.preview || row.unread ? (
                <div className="chat-row-bottom">
                  <p className={`mail-snippet chat-row-preview${row.unread ? " unread" : ""}`}>
                    {replaceShortcodes(row.preview)}
                  </p>
                  {row.unread ? (
                    <span
                      className={`chat-unread-badge${row.unreadCount ? "" : " chat-unread-badge-dot"}`}
                      aria-label={unreadLabel(row.unreadCount)}
                    >
                      {unreadBadgeText(row.unreadCount)}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </span>
          </span>
        </button>
      </li>
    );
  }

  function renderNeedsRow(needs: NeedsRow): ReactNode {
    const when = formatListWhen(needs.item.at, clockFormat);
    const active = Boolean(needs.row && !needs.item.threadTs && selectedKey === needs.row.key);
    const faceType: ChatType = needs.row?.type || "direct";
    return (
      <li key={needs.key}>
        <button
          type="button"
          className={`mail-row chat-row${active ? " active" : ""}`}
          aria-current={active ? "true" : undefined}
          onClick={() => openNeedsRow(needs)}
        >
          <span className="chat-row-main">
            <ChatListFace
              type={faceType}
              title={needs.title}
              avatarUrl={needs.row?.channel.avatarUrl}
              faceKey={needs.row?.channel.id || needs.key}
            />
            <span className="chat-row-body">
              {showAccountChip ? (
                <span className="mail-row-account">Slack · {accountLabel(needs.account)}</span>
              ) : null}
              <div className="mail-row-top">
                <span className="mail-row-from-wrap">
                  <strong>{needs.title}</strong>
                </span>
                {when ? (
                  <div className="mail-row-indicators">
                    <span className="mail-row-time">{when}</span>
                  </div>
                ) : null}
              </div>
              <p className="mail-snippet chat-row-preview">
                {needs.item.threadTs
                  ? `In a thread · ${replaceShortcodes(needs.preview)}`
                  : replaceShortcodes(needs.preview)}
              </p>
            </span>
          </span>
        </button>
      </li>
    );
  }

  const chatDrawer = chatDrawerOpen ? (
    <div className="mail-emails-scrim phone-only" onClick={() => setChatDrawerOpen(false)}>
      <aside
        className="mail-emails-drawer"
        aria-label="Chat"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mail-emails-drawer-head">
          <strong>Chat</strong>
          <button
            type="button"
            className="mail-emails-close"
            aria-label="Close Chat menu"
            onClick={() => setChatDrawerOpen(false)}
          >
            <X size={16} />
          </button>
        </div>
        <div className="module-drawer-body">
          <p className="mail-nav-kicker">Chats</p>
          {accounts.length ? (
            renderTree()
          ) : (
            <p className="muted pad">Connect Slack or Zoho Cliq to load channels.</p>
          )}
        </div>
        <ModuleDrawerAdd
          to="/settings/connections/chat"
          label="Connect chat account"
          onNavigate={() => setChatDrawerOpen(false)}
        />
      </aside>
    </div>
  ) : null;

  if (!ready) {
    return (
      <div className="page-pad">
        <h1>Chat</h1>
        <p className="muted">Loading chat status…</p>
        {chatDrawer}
      </div>
    );
  }

  if (showSetup) {
    return (
      <div className="page-pad chat-setup-page">
        <header className="chat-page-head">
          <h1>Chat</h1>
          <p className="muted">
            Connect Slack first, then Zoho Cliq. Multiple logins per provider are supported.
          </p>
        </header>
        {notice ? <p className="banner-info">{notice}</p> : null}
        {error ? (
          <p className="banner-error" role="alert">
            {error}
          </p>
        ) : null}
        <section className="settings-card chat-setup-card">
          <h2>{slackReady ? "Connect Slack" : "Slack App Setup Required"}</h2>
          {slackReady ? (
            <p className="muted">
              Hub is ready. Sign in to a Slack workspace — Connect again later to add a second
              Slack login without replacing the first.
            </p>
          ) : (
            <>
              <p className="muted">Slack app credentials are not on the API host yet. Do this once:</p>
              <ol className="settings-steps">
                {setupSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </>
          )}
          <div className="settings-btn-row">
            <button
              type="button"
              className="primary-btn"
              disabled={checking}
              onClick={() => void checkSetup()}
            >
              {checking ? "Checking…" : slackReady ? "Connect Slack" : "Check Setup"}
            </button>
            <button
              type="button"
              className="secondary-btn"
              disabled={checking || !cliqReady}
              onClick={() => void connectProvider("cliq")}
              title={cliqReady ? undefined : "Add Cliq OAuth credentials on the API host first"}
            >
              Connect Cliq
            </button>
            <Link className="secondary-btn settings-link-btn" to="/settings/connections/chat">
              Open Settings
            </Link>
          </div>
          {!cliqReady ? (
            <p className="muted tight">
              Cliq Connect unlocks after Zoho Cliq client credentials land on the API host (see
              Connections → Chat).
            </p>
          ) : null}
        </section>
        {customerSetup?.configured && customerSetup.installUrl ? (
          <section className="settings-card chat-setup-card">
            <h2>ATW Hub for Slack</h2>
            <p className="muted">
              Add the ATW Hub app to your Slack workspace. Hub loads a conversation from Slack when you
              open it and doesn&apos;t store message text.
            </p>
            <div className="settings-btn-row">
              <a
                className="primary-btn settings-link-btn"
                href={customerSetup.installUrl}
                data-hub-same-tab="1"
              >
                Add ATW Hub to Slack
              </a>
            </div>
          </section>
        ) : null}
        {chatDrawer}
      </div>
    );
  }

  const sortLabel = `${SORT_FIELDS.find((f) => f.id === sortField)?.label || "Unread first"} ${sortDir === "desc" ? "↓" : "↑"}`;
  const filterLabel = FILTER_OPTIONS.find((f) => f.id === filter)?.label || "All Chats";

  return (
    <div className={`mail-layout chat-layout${navCollapsed ? " mail-nav-collapsed" : ""}`}>
      <aside
        className={`mail-nav-pane desktop-only${navCollapsed ? " mail-nav-pane-collapsed" : ""}`}
        aria-label="Chat accounts and folders"
      >
        <div className="mail-nav-compose-row">
          {navCollapsed ? (
            <>
              {hasSlack ? (
                <button
                  type="button"
                  className="mail-compose-btn mail-compose-icon"
                  title="New Chat"
                  aria-label="New Chat"
                  onClick={openNewChat}
                >
                  <SquarePen size={18} />
                </button>
              ) : (
                <Link
                  className="mail-compose-btn mail-compose-icon"
                  to="/settings/connections/chat"
                  title="Connect chat account"
                  aria-label="Connect chat account"
                >
                  <Plus size={18} />
                </Link>
              )}
              <button
                type="button"
                className="mail-nav-collapse-btn"
                aria-label="Expand chats"
                title="Expand"
                onClick={toggleNav}
              >
                <ChevronRight size={16} />
              </button>
            </>
          ) : (
            <>
              {hasSlack ? (
                <button type="button" className="mail-compose-btn" onClick={openNewChat}>
                  <Plus size={16} />
                  New Chat
                </button>
              ) : (
                <Link className="mail-compose-btn" to="/settings/connections/chat">
                  <Plus size={16} />
                  Connect Chat
                </Link>
              )}
              <button
                type="button"
                className="mail-nav-collapse-btn"
                aria-label="Collapse chats"
                title="Collapse"
                onClick={toggleNav}
              >
                <ChevronLeft size={16} />
              </button>
            </>
          )}
        </div>
        {!navCollapsed ? (
          <>
            <p className="mail-nav-kicker">Chats</p>
            {renderTree()}
            <Link className="chat-tree-connect" to="/settings/connections/chat">
              <Plus size={14} aria-hidden />
              Connect chat account
            </Link>
          </>
        ) : null}
      </aside>
      {!navCollapsed ? (
        <PaneResizer id="chat.nav" cssVar="--mail-nav-w" min={180} max={380} label="Resize chat sidebar" />
      ) : null}

      <section className="mail-list-pane" aria-label={viewLabel}>
        <div className="mail-list-toolbar chat-list-toolbar">
          <div className="mail-sort-wrap">
            <button
              type="button"
              className="mail-sort-trigger"
              aria-expanded={sortOpen}
              onClick={() => setSortOpen((v) => !v)}
            >
              <strong>{sortLabel}</strong>
              <span className="muted"> · {filterLabel}</span>
              <ChevronDown size={14} />
            </button>
            {sortOpen ? (
              <div className="mail-sort-sheet" role="dialog" aria-label="Sort and filter">
                <p className="mail-nav-kicker">Sort</p>
                {SORT_FIELDS.map((opt) => {
                  const active = sortField === opt.id;
                  const DirIcon = sortDir === "desc" ? ArrowDown : ArrowUp;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={active ? "mail-sort-option active" : "mail-sort-option"}
                      aria-pressed={active}
                      aria-label={
                        active
                          ? `${opt.label}, ${sortDirAria(opt.id, sortDir)}. Activate to reverse.`
                          : `Sort by ${opt.label}`
                      }
                      onClick={() => selectSortField(opt.id)}
                    >
                      <span className="mail-sort-option-label">{opt.label}</span>
                      <span className="mail-sort-option-meta" aria-hidden>
                        {active ? (
                          <>
                            <DirIcon size={14} strokeWidth={2.5} className="mail-sort-dir-icon" />
                            <Check size={14} strokeWidth={2.5} className="mail-sort-check" />
                          </>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
                <p className="mail-nav-kicker">Filter</p>
                <div className="mail-filter-row">
                  {FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      className={filter === opt.id ? "mail-chip active" : "mail-chip"}
                      onClick={() => {
                        setFilter(opt.id);
                        setSortOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="icon-btn"
            aria-label="Refresh"
            disabled={channelsLoading}
            onClick={refreshAll}
          >
            <RefreshCw size={18} className={channelsLoading ? "spinning" : undefined} />
          </button>
        </div>

        <p className="chat-list-context">{viewLabel}</p>

        {newDmOpen && hasSlack ? (
          <ChatNewDmPanel
            accounts={slackAccounts}
            initialAccountId={newDmAccountId}
            onClose={() => setNewDmOpen(false)}
            onOpened={handleDmOpened}
          />
        ) : null}
        {notice ? <p className="banner-info">{notice}</p> : null}
        {error ? <p className="banner-error">{error}</p> : null}
        {listErrors.map((message) => (
          <p key={message} className="banner-error">
            {message}
          </p>
        ))}
        {checkingAccounts.length ? (
          <p className="muted chat-activity-progress">
            {checkingAccounts
              .map((a) => {
                const snapshot = activityByAccount[a.id]!;
                return snapshot.mode === "search" || !snapshot.total
                  ? `Checking ${chatAccountLabel(a)}…`
                  : `Checking ${chatAccountLabel(a)}… ${snapshot.checked} of ${snapshot.total}`;
              })
              .join(" · ")}
          </p>
        ) : null}
        {view.scope === "home" && limitedAccounts.length ? (
          <p className="banner-info chat-activity-note">
            Home can&apos;t see new messages in {limitedAccounts.map(chatAccountLabel).join(" or ")} yet. Slack
            limits apps that aren&apos;t in the Slack Marketplace, so Hub needs permission to search there:
            reconnect it in <Link to="/settings/connections/chat">Connections</Link> and click Allow.
          </p>
        ) : null}
        {view.scope === "home" && pilotCustomers.length ? (
          <p className="banner-info chat-activity-note">{NEEDS_YOU_PILOT_MESSAGE}</p>
        ) : null}
        {[...needsYouErrors, ...needsYouPartial].map((message) => (
          <p key={message} className="banner-error chat-activity-note">
            {message}
          </p>
        ))}
        {channelsLoading && !listedCount && !homeNeedsCount ? <p className="muted pad">Loading chats…</p> : null}
        {!channelsLoading && !listedCount && !homeNeedsCount ? (
          <p className="muted pad">
            {searchQuery
              ? `No chats match “${searchQuery}”.`
              : view.scope === "home"
                ? homeEmptyText
                : filter !== "all"
                  ? "No chats match this filter."
                  : view.scope === "account"
                    ? view.folder === ALL_MESSAGES
                      ? "No chats in this account yet."
                      : `No ${viewFolderLabel.toLowerCase()} in this account.`
                    : "No chats yet."}
          </p>
        ) : null}

        {view.scope === "home" &&
        !searchQuery &&
        !channelsLoading &&
        !checkingAccounts.length &&
        !homeNeedsCount &&
        listedCount &&
        sections.every((section) => section.id === "jump" || !section.rows.length) ? (
          <p className="muted pad">You&apos;re all caught up — nothing new this week.</p>
        ) : null}
        <ul className="mail-thread-list">
          {homeNeedsCount ? (
            <li className="chat-list-group">
              <p className="mail-nav-kicker chat-list-section">Needs you · {homeNeedsCount}</p>
              <ul className="chat-list-group-rows">{needsRows.map(renderNeedsRow)}</ul>
            </li>
          ) : null}
          {sections.map((section) =>
            section.rows.length ? (
              <li key={section.id} className="chat-list-group">
                {section.label ? (
                  <p className="mail-nav-kicker chat-list-section">
                    {section.label} · {section.rows.length}
                  </p>
                ) : null}
                <ul className="chat-list-group-rows">{section.rows.map(renderRow)}</ul>
              </li>
            ) : null,
          )}
        </ul>
      </section>
      <PaneResizer id="chat.list" cssVar="--mail-list-w" min={260} max={720} label="Resize conversation list" />

      <section
        className={`mail-reader-pane chat-reader-pane${selectedKey ? " open" : ""}`}
        data-pane-main
        data-mail-reader={selectedKey ? "open" : "empty"}
        aria-live="polite"
      >
        {!selectedKey || !selectedRow ? (
          <div className="mail-empty-reader chat-empty-reader">
            <p className="chat-empty-reader-title">Select a conversation</p>
            <p className="chat-empty-reader-copy muted">
              Pick a chat from the list, or use New Chat to start a direct message.
            </p>
          </div>
        ) : (
          <div className="chat-thread">
            <button
              type="button"
              className="phone-only text-btn mail-reader-back"
              onClick={() => setSelectedKey(null)}
            >
              ← Back to {backLabel}
            </button>
            <header className="chat-thread-head">
              <h2>{selectedRow.title}</h2>
              <p className="mail-reader-meta muted">
                {providerName(threadProvider)} · {accountLabel(selectedRow.account)} ·{" "}
                {typeLabel(selectedRow.type)}
              </p>
              {selectedRow.type !== "direct" && selectedRow.channel.topic ? (
                <p className="chat-thread-topic muted">{selectedRow.channel.topic}</p>
              ) : null}
            </header>
            <div className="chat-thread-messages" ref={messagesRef}>
              {customerThread?.limited && customerThread.hasMore && messages.length ? (
                <p className="chat-thread-note muted">
                  Showing the latest messages.{" "}
                  {customerThread.permalink ? <a href={customerThread.permalink}>Open in Slack</a> : "Open in Slack"}{" "}
                  for older ones.
                </p>
              ) : null}
              {customerThread && !customerThread.limited && customerThread.nextCursor && messages.length ? (
                <div className="chat-thread-older">
                  <button
                    type="button"
                    className="secondary-btn"
                    disabled={olderLoading}
                    onClick={() => void loadOlderMessages()}
                  >
                    {olderLoading ? "Loading…" : "Load older messages"}
                  </button>
                  {olderError ? <p className="banner-error">{olderError}</p> : null}
                </div>
              ) : null}
              {messagesLoading && !messages.length ? (
                <p className="muted pad">Loading messages…</p>
              ) : null}
              {rateLimitSeconds ? (
                <p className="banner-info chat-thread-note">
                  Slack is limiting how often ATW Hub can load conversations. Trying again in{" "}
                  {rateLimitSeconds} second{rateLimitSeconds === 1 ? "" : "s"}.
                  {customerThread?.permalink ? (
                    <>
                      {" "}
                      <a className="banner-reconnect-link" href={customerThread.permalink}>
                        Open in Slack
                      </a>
                    </>
                  ) : null}
                </p>
              ) : null}
              {messagesError ? (
                <p className="banner-error">
                  {messagesError}
                  {customerThread?.permalink ? (
                    <>
                      {" "}
                      <a className="banner-reconnect-link" href={customerThread.permalink}>
                        Open in Slack
                      </a>
                    </>
                  ) : null}
                </p>
              ) : null}
              {!messagesLoading && !messagesError && !rateLimitSeconds && !messages.length ? (
                <div className="chat-thread-empty">
                  <p>No messages in this conversation yet.</p>
                </div>
              ) : null}
              {threadRoot ? (
                <div className="chat-thread-view">
                  <button type="button" className="text-btn" onClick={closeThread}>
                    ← Back to {selectedRow.title}
                  </button>
                  <p className="chat-thread-kicker">
                    Thread{threadRoot.author ? ` · ${threadRoot.author}` : ""}
                    {caps.permalink ? (
                      <>
                        {" · "}
                        <button type="button" className="text-btn" onClick={() => openInProvider(threadRoot)}>
                          Open in {providerName(threadProvider)}
                        </button>
                      </>
                    ) : null}
                  </p>
                  {threadRoot.text || threadRoot.images?.length || threadRoot.files?.length ? (
                    <ChatMessageBubbles
                      messages={[threadRoot]}
                      account={selectedRow.account}
                      onToggleReaction={caps.reactions ? (m, r) => void toggleReaction(m, r.name || "") : undefined}
                      onHold={setMenuMessage}
                    />
                  ) : null}
                  {threadLoading && !threadMessages.length ? <p className="muted pad">Loading replies…</p> : null}
                  {threadError ? <p className="banner-error">{threadError}</p> : null}
                  {!threadLoading && !threadError && !threadMessages.length ? (
                    <p className="muted pad">No replies yet. Start the thread below.</p>
                  ) : null}
                  <ChatMessageBubbles
                    messages={threadMessages}
                    account={selectedRow.account}
                    onEdit={caps.edit ? handleEditMessage : undefined}
                    onDelete={caps.remove ? requestDeleteMessage : undefined}
                    onToggleReaction={caps.reactions ? (m, r) => void toggleReaction(m, r.name || "") : undefined}
                    onHold={setMenuMessage}
                  />
                </div>
              ) : messages.length ? (
                <ChatMessageBubbles
                  messages={messages}
                  account={selectedRow.account}
                  onEdit={caps.edit ? handleEditMessage : undefined}
                  onDelete={caps.remove ? requestDeleteMessage : undefined}
                  onOpenThread={caps.threads || caps.permalink ? openThread : undefined}
                  threadsInHub={caps.threads}
                  onToggleReaction={caps.reactions ? (m, r) => void toggleReaction(m, r.name || "") : undefined}
                  onHold={setMenuMessage}
                />
              ) : null}
              {typingNames.length ? (
                <p className="chat-typing" aria-live="polite">
                  {typingNames.length === 1
                    ? `${typingNames[0]} is typing…`
                    : `${typingNames.slice(0, 2).join(" and ")} are typing…`}
                </p>
              ) : null}
            </div>
            {threadRoot && caps.send ? (
              <ChatComposer
                conversationKey={`${selectedKey}:thread:${threadRoot.id}`}
                placeholder="Reply in thread"
                onSend={handleThreadReply}
              />
            ) : threadProvider === "cliq" || !caps.send ? (
              <p className="chat-composer-readonly muted">
                {threadProvider === "cliq"
                  ? "Zoho Cliq is view-only in Hub. Reply in Cliq."
                  : "Sending is not available for this chat provider."}
              </p>
            ) : (
              <ChatComposer
                conversationKey={selectedKey}
                placeholder={`Message ${selectedRow.title}`}
                onSend={handleSend}
              />
            )}
          </div>
        )}
      </section>

      {menuMessage ? (
        <ChatMessageMenu
          message={menuMessage}
          onClose={() => setMenuMessage(null)}
          onReact={caps.reactions && menuMessage.ts ? (m, name) => void toggleReaction(m, name) : undefined}
          onReply={
            caps.threads && menuMessage.ts && !menuMessage.parentTs && !threadRoot ? openThread : undefined
          }
          onOpenInProvider={caps.permalink && menuMessage.ts ? openInProvider : undefined}
          providerLabel={providerName(threadProvider)}
          onCopy={(message) => {
            void navigator.clipboard.writeText(message.text);
            setNotice("Message copied.");
            setMenuMessage(null);
          }}
        />
      ) : null}
      {pendingDelete ? (
        <div
          className="cal-confirm-scrim"
          role="presentation"
          onClick={() => {
            if (!deleting) setPendingDelete(null);
          }}
        >
          <div
            className="cal-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="chat-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="chat-delete-title">Delete message?</h3>
            <p className="cal-confirm-body">
              {pendingDelete.text ? (
                <>
                  <strong className="chat-delete-preview">{pendingDelete.text}</strong>
                  <br />
                </>
              ) : null}
              This deletes it in {providerName(threadProvider)} for everyone in the conversation. This cannot be
              undone.
            </p>
            {deleteError ? (
              <p className="banner-error" role="alert">
                {deleteError}
              </p>
            ) : null}
            <div className="cal-confirm-actions">
              <button
                type="button"
                className="secondary-btn"
                disabled={deleting}
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-btn"
                disabled={deleting}
                onClick={() => void confirmDeleteMessage()}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {chatDrawer}
    </div>
  );
}
